import { useState } from 'react';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { auth } from '../firebase-config';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

export default function DebugAuthPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    setLogs((prev) => [...prev, `${new Date().toISOString()}: ${message}`]);
  };

  const testMagicLink = async () => {
    addLog('Starting magic link test...');

    try {
      addLog(`Firebase Auth initialized: ${auth ? 'YES' : 'NO'}`);
      addLog(`Current user: ${auth.currentUser?.email || 'NONE'}`);
      addLog(`Email to send to: ${email}`);

      const actionCodeSettings = {
        url: `${window.location.origin}/auth/verify`,
        handleCodeInApp: true,
      };

      addLog('Calling sendSignInLinkToEmail...');

      await sendSignInLinkToEmail(auth, email, actionCodeSettings);

      addLog('✅ SUCCESS! Email sent successfully.');
      addLog('Check your email inbox (and spam folder)');

      window.localStorage.setItem('emailForSignIn', email);
    } catch (error: any) {
      addLog(`❌ ERROR: ${error.code}`);
      addLog(`Message: ${error.message}`);

      if (error.code === 'auth/invalid-email') {
        addLog('→ The email address is invalid');
      } else if (error.code === 'auth/unauthorized-continue-uri') {
        addLog('→ The continue URL is not authorized in Firebase Console');
      } else if (error.code === 'auth/operation-not-allowed') {
        addLog('→ Email/Link sign-in is NOT ENABLED in Firebase Console');
        addLog('→ Go to Firebase Console > Authentication > Sign-in method');
        addLog('→ Enable "Email/Password" and toggle ON "Email link (passwordless sign-in)"');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">Magic Link Debug Page</h1>
          <p className="text-muted-foreground">
            Test Firebase Auth magic link configuration
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold">Configuration Check</h2>
          <div className="space-y-2 font-mono text-sm">
            <div>Firebase Project: <code className="bg-muted px-2 py-1 rounded">{auth.app.options.projectId}</code></div>
            <div>Auth Domain: <code className="bg-muted px-2 py-1 rounded">{auth.app.options.authDomain}</code></div>
            <div>Current Origin: <code className="bg-muted px-2 py-1 rounded">{window.location.origin}</code></div>
            <div>Redirect URL: <code className="bg-muted px-2 py-1 rounded">{window.location.origin}/auth/verify</code></div>
            <div>Current User: <code className="bg-muted px-2 py-1 rounded">{user?.email || 'Not logged in'}</code></div>
            {user && (
              <div className="pt-2 border-t border-border">
                <div className="font-semibold mb-1">Your Firebase Auth UID:</div>
                <code className="bg-yellow-500/10 border border-yellow-500/20 px-2 py-1 rounded text-yellow-700 dark:text-yellow-300">
                  {user.uid}
                </code>
                <div className="text-xs text-muted-foreground mt-1">
                  Use this as the Document ID when creating your super admin document in Firestore
                </div>
              </div>
            )}
          </div>
        </div>

        {user && (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-blue-600 dark:text-blue-400">Manual Super Admin Setup Required</h2>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Due to Firestore security rules, the first super admin must be created manually via the Firebase Console. Follow these steps:
            </p>
            <ol className="text-sm space-y-2 text-blue-700 dark:text-blue-300 list-decimal list-inside">
              <li>Go to <a href="https://console.firebase.google.com/project/productworkshop-dev/firestore" target="_blank" rel="noopener noreferrer" className="underline font-medium">Firestore Console</a></li>
              <li>Click "Start collection" or navigate to the <code className="bg-muted px-1 py-0.5 rounded">admins</code> collection</li>
              <li>Click "Add document"</li>
              <li>Set Document ID to: <code className="bg-muted px-1 py-0.5 rounded font-mono text-xs">{user.uid}</code></li>
              <li>Add these fields:
                <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                  <li><code className="bg-muted px-1 py-0.5 rounded">userId</code> (string): <code className="bg-muted px-1 py-0.5 rounded">{user.uid}</code></li>
                  <li><code className="bg-muted px-1 py-0.5 rounded">email</code> (string): <code className="bg-muted px-1 py-0.5 rounded">{user.email}</code></li>
                  <li><code className="bg-muted px-1 py-0.5 rounded">role</code> (string): <code className="bg-muted px-1 py-0.5 rounded">super_admin</code></li>
                  <li><code className="bg-muted px-1 py-0.5 rounded">createdAt</code> (timestamp): Set to current time</li>
                </ul>
              </li>
              <li>Click "Save"</li>
              <li>Navigate to <a href="/admin" className="underline font-medium">/admin</a> to access the Super Admin Portal</li>
            </ol>
            <div className="bg-blue-600/10 border border-blue-600/20 rounded p-3 mt-4">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                💡 <strong>Why manual?</strong> Firestore security rules prevent creating super admins from the client for security. In production, this would be done via a secure Cloud Function.
              </p>
            </div>
          </div>
        )}

        {!user && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-6">
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              You must be logged in to become a super admin. Use the magic link form below or go to <a href="/login" className="underline font-medium">/login</a>
            </p>
          </div>
        )}

        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold">Test Magic Link</h2>
          <div className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <Button onClick={testMagicLink} disabled={!email}>
              Send Test Magic Link
            </Button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <h2 className="text-xl font-semibold">Debug Logs</h2>
          <div className="bg-muted rounded-lg p-4 max-h-96 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-muted-foreground text-sm">No logs yet. Click "Send Test Magic Link" to start.</p>
            ) : (
              <pre className="text-xs whitespace-pre-wrap font-mono">
                {logs.join('\n')}
              </pre>
            )}
          </div>
          {logs.length > 0 && (
            <Button variant="outline" onClick={() => setLogs([])}>
              Clear Logs
            </Button>
          )}
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-6 space-y-2">
          <h3 className="font-semibold text-yellow-600 dark:text-yellow-400">
            Common Issues:
          </h3>
          <ul className="text-sm space-y-1 text-yellow-700 dark:text-yellow-300">
            <li>• Email/Link sign-in not enabled in Firebase Console</li>
            <li>• Email went to spam folder</li>
            <li>• Firebase project not in production mode (test mode limits emails)</li>
            <li>• Invalid email address</li>
            <li>• Firebase quota exceeded</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
